import React, { useState, useRef, useEffect, useCallback } from "react";
import { GrantsLabelItem } from "../../../services/grantsApi";
import { getColorConfig } from "./labelColors";
import { getSafeErrorMessage, MAX_LABELS } from "./useCallLabels";
import { useToast } from "../../../contexts/ToastContext";
import { useClickOutside } from "../../../hooks/useClickOutside";

interface CallLabelCellProps {
  callId: string;
  labels: GrantsLabelItem[];
  currentLabel?: { labelId: string; labelName: string; color: string } | null;
  onSetLabel: (callId: string, labelId: string | null) => Promise<unknown>;
  onCreateAndAssign: (name: string, callId: string) => Promise<unknown>;
  onManageLabels: () => void;
}

/**
 * Label cell component for the Calls table.
 *
 * Displays current label as a colored badge, or a dash if none.
 * Click opens a dropdown with:
 * - List of existing labels (sorted by sortOrder)
 * - "Usuń etykietę" when a label is assigned
 * - Separator
 * - "Nowa etykieta..." for inline creation
 * - "Zarządzaj etykietami" link to modal
 *
 * Empty state: opens directly in inline creation mode.
 * Error handling: shows toast, keeps dropdown open.
 */
const CallLabelCell: React.FC<CallLabelCellProps> = ({
  callId,
  labels,
  currentLabel,
  onSetLabel,
  onCreateAndAssign,
  onManageLabels,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newLabelName, setNewLabelName] = useState("");
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { addToast } = useToast();

  const handleClickOutside = useCallback(() => {
    setIsOpen(false);
    setIsCreating(false);
    setNewLabelName("");
    setInlineError(null);
  }, []);

  useClickOutside(ref, handleClickOutside, isOpen);

  // Auto-focus input when entering creation mode
  useEffect(() => {
    if (isCreating && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isCreating]);

  // When dropdown opens with no labels, open in creation mode (empty state)
  useEffect(() => {
    if (isOpen && labels.length === 0) {
      setIsCreating(true);
    }
  }, [isOpen, labels.length]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isOpen) {
      setIsOpen(true);
      setIsCreating(false);
      setNewLabelName("");
      setInlineError(null);
    } else {
      setIsOpen(false);
      setIsCreating(false);
      setNewLabelName("");
      setInlineError(null);
    }
  };

  const handleSelectLabel = async (labelId: string | null) => {
    setSaving(true);
    try {
      await onSetLabel(callId, labelId);
      setIsOpen(false);
      setIsCreating(false);
    } catch (err) {
      // Show error toast, keep dropdown open (per spec)
      addToast({
        type: "error",
        message: getSafeErrorMessage(err),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleStartCreation = () => {
    setIsCreating(true);
    setNewLabelName("");
    setInlineError(null);
  };

  const handleCreateSubmit = async () => {
    const trimmedName = newLabelName.trim();
    if (!trimmedName) return;

    // Client-side duplicate check (case-insensitive)
    const duplicate = labels.find(
      (l) => l.name.toLowerCase() === trimmedName.toLowerCase()
    );
    if (duplicate) {
      setInlineError("Etykieta o tej nazwie już istnieje");
      return;
    }

    setSaving(true);
    setInlineError(null);
    try {
      await onCreateAndAssign(trimmedName, callId);
      setIsOpen(false);
      setIsCreating(false);
      setNewLabelName("");
    } catch (err) {
      setInlineError(getSafeErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCreateSubmit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsCreating(false);
      setNewLabelName("");
      setInlineError(null);
    }
  };

  const handleManageClick = () => {
    setIsOpen(false);
    setIsCreating(false);
    onManageLabels();
  };

  const colorCfg = currentLabel ? getColorConfig(currentLabel.color) : null;
  const isAtLimit = labels.length >= MAX_LABELS;
  const sortedLabels = [...labels].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
  );

  return (
    <div ref={ref} className="relative">
      {/* Trigger: shows label badge or dash */}
      <button
        ref={triggerRef}
        onClick={handleToggle}
        className="flex items-center gap-1.5 text-sm hover:bg-neutral-100 rounded px-1.5 py-0.5 cursor-pointer max-w-[140px] min-w-[60px]"
        title={currentLabel ? currentLabel.labelName : "Przypisz etykietę"}
        aria-label={currentLabel ? `Zmień etykietę: ${currentLabel.labelName}` : "Przypisz etykietę"}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        {currentLabel && colorCfg ? (
          <span
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${colorCfg.badge}`}
          >
            <span
              className={`w-2 h-2 rounded-full ${colorCfg.dot}`}
              aria-hidden="true"
            />
            <span className="truncate max-w-[90px]">
              {currentLabel.labelName}
            </span>
          </span>
        ) : (
          <span className="text-neutral-400 text-xs">&mdash;</span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="absolute z-50 mt-1 w-56 bg-white border border-neutral-200 rounded-md shadow-lg py-1 left-0"
          role="menu"
          aria-label="Wybierz etykietę"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Labels list (when not in creation-only mode from empty state) */}
          {!isCreating || labels.length > 0 ? (
            <>
              {sortedLabels.map((label) => {
                const cfg = getColorConfig(label.color);
                const isSelected = currentLabel?.labelId === label.id;
                return (
                  <button
                    key={label.id}
                    onClick={() => handleSelectLabel(label.id)}
                    disabled={saving}
                    role="menuitemradio"
                    aria-checked={isSelected}
                    className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-neutral-50 focus:bg-neutral-100 focus:outline-none disabled:opacity-50 ${
                      isSelected ? "bg-neutral-100 font-medium" : ""
                    }`}
                  >
                    <span
                      className={`w-2.5 h-2.5 rounded-full shrink-0 ${cfg.dot}`}
                    />
                    <span className="truncate">{label.name}</span>
                  </button>
                );
              })}

              {/* Remove label option (when label assigned) */}
              {currentLabel && (
                <>
                  <hr className="my-1 border-neutral-200" />
                  <button
                    onClick={() => handleSelectLabel(null)}
                    disabled={saving}
                    role="menuitem"
                    className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 focus:bg-red-100 focus:outline-none disabled:opacity-50"
                  >
                    Usuń etykietę
                  </button>
                </>
              )}

              {/* Separator before actions */}
              <hr className="my-1 border-neutral-200" />
            </>
          ) : null}

          {/* Inline creation mode */}
          {isCreating ? (
            <div className="px-3 py-2 space-y-1">
              <input
                ref={inputRef}
                type="text"
                value={newLabelName}
                onChange={(e) => {
                  setNewLabelName(e.target.value);
                  setInlineError(null);
                }}
                onKeyDown={handleInputKeyDown}
                placeholder="Nazwa etykiety"
                maxLength={50}
                disabled={saving}
                className="w-full text-xs border border-neutral-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                aria-label="Nazwa nowej etykiety"
              />
              {inlineError && (
                <p className="text-xs text-red-600" role="alert">
                  {inlineError}
                </p>
              )}
              <p className="text-xs text-neutral-500">
                Enter = zapisz, Escape = anuluj
              </p>
            </div>
          ) : (
            <button
              onClick={handleStartCreation}
              disabled={isAtLimit}
              aria-disabled={isAtLimit}
              title={
                isAtLimit ? "Osiągnięty limit 10 etykiet" : "Utwórz nową etykietę"
              }
              className={`w-full text-left px-3 py-1.5 text-xs focus:outline-none ${
                isAtLimit
                  ? "text-neutral-400 cursor-not-allowed"
                  : "text-primary-600 hover:bg-primary-50 font-medium"
              }`}
            >
              Nowa etykieta...
            </button>
          )}

          {/* Label count indicator */}
          <div className="px-3 py-1 text-xs text-neutral-500">
            {labels.length} / {MAX_LABELS} etykiet
          </div>

          {/* Manage labels link */}
          <button
            onClick={handleManageClick}
            role="menuitem"
            className="w-full text-left px-3 py-1.5 text-xs text-neutral-600 hover:bg-neutral-50 focus:outline-none"
          >
            Zarządzaj etykietami
          </button>
        </div>
      )}
    </div>
  );
};

export default CallLabelCell;
